<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import TypesContainersRepository from '../../repositories/typesContainers.repository';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPagination } from '../../types/pagination.type';
import { ITypeContainers } from '../../types/typeContainers.type';
import { useToast } from '../../composables/useToast';
import { useI18n } from 'vue-i18n'
import DataTable from '@components/DataTable.vue';

const isLoadingTypesContainers = ref<boolean>(false);

const { t } = useI18n();
const store = Store();
const { $toast } = useToast();
const { typesContainers: typeContainersPinia }: any = storeToRefs(store);
const typeContainers = ref<ITypeContainers[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedTypeContainer = ref<ITypeContainers | null>(null);
const isViewActive = ref<boolean>(true);
const fieldsTableInactive: any = ref([
  {
    key: 'id',
    label: t('global.id'),
    sortable: true,
  },
  {
    key: 'typeName',
    label: t('global.nombre'),
  },
  {
    key: 'restore',
    label: t('global.restaurar'),
  },
]);
const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: t('global.id'),
    sortable: true,
  },
  {
    key: 'typeName',
    label: t('global.nombre'),
  },
  {
    key: 'typeContainerInfo',
    label: t('global.editar'),
  },
  {
    key: 'delete',
    label: t('global.eliminar'),
  },
]);

// Load TypesContainers ABM
const getTypesContainersFromAPI = async (page: number, deleted: boolean = false) => {
  isLoadingTypesContainers.value = true;
  try {
    const { data } = await TypesContainersRepository.getTypesContainers({ page, deleted });
    if (data && Array.isArray(data?.typeContainers)) {
      paginationData.value = data.pagination;
      return data.typeContainers;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching typeContainers:', error);
    return [];
  } finally {
    isLoadingTypesContainers.value = false;
  }
};

const updateTypesContainers = (newTypesContainers: ITypeContainers[]) => {
  typeContainers.value = newTypesContainers;
  store.setProp({ key: 'typesContainers', value: newTypesContainers });
};

const loadTypesContainers = async () => {
  const newTypesContainers = await getTypesContainersFromAPI(1);
  updateTypesContainers(newTypesContainers);
};

onMounted(loadTypesContainers);

// Create inputs from modals
const fieldsInputTypeContainer = computed(() => {
  return [
    {
      key: 'typeName',
      title: t('ajustes.contenedores.tipo_de_contenedor'),
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
		messages: {
		  required: t('global.es_obligatorio'),
		},
      },
      placeholder: t('ajustes.contenedores.tipo_de_contenedor'),
      value: selectedTypeContainer.value?.typeName,
      cols: 12,
    },
  ];
});

// UI
const onChangePage = async (page: number) => {
  getTypesContainersFromAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = t('ajustes.contenedores.agregar_tipo_de_contenedor');
  isModalOpen.value = true;
  modalType.value = 'modalCreateTypeContainer';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedTypeContainer.value = null;
};

const handleActive = async () => {
  const typeContainers = await getTypesContainersFromAPI(1);
  updateTypesContainers(typeContainers);
  isViewActive.value = true;
};

const handleInactive = async () => {
  const typeContainersDeleted = await getTypesContainersFromAPI(1, true);
  updateTypesContainers(typeContainersDeleted);
  isViewActive.value = false;
};

const handleOpenModalDialog = (item: ITypeContainers) => {
  modalTitle.value = isViewActive.value
    ? `${t('ajustes.contenedores.borrar_el_contenedor_con_el_id')}: ${item.id}`
    : `${t('ajustes.contenedores.restaurar_el_contenedor_con_el_id')}: ${item.id}`;
  modalType.value = 'modalDialog';
  selectedTypeContainer.value = item;
  isModalOpen.value = true;
};

//CRUD
const handleCreateTypeContainer = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  try {
    const data = await TypesContainersRepository.createTypeContainer(items);
    store.setProp({ key: 'typesContainers', value: [...typeContainers.value, data] });
    $toast?.success(t('ajustes.contenedores.tipo_de_contenedor_agregado_con_exito'));
  } catch (error) {
    $toast?.error(t('global.ocurrio_un_error'));
  }

  handleClose();
};

const handleSelectTypeContainer = (typeContainerData: ITypeContainers) => {
  modalTitle.value = t('ajustes.contenedores.actualizar_contenedor');
  (isModalOpen.value = true), (modalType.value = 'modalUpdateTypeContainer');
  selectedTypeContainer.value = typeContainerData;
};

const handleUpdateTypeContainer = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedTypeContainer.value) return;
  const typeContainerForUpdate = {
    ...items,
    id: selectedTypeContainer.value.id,
  };

  try {
    const data = await TypesContainersRepository.updateTypeContainer(typeContainerForUpdate);
    let index = store.typesContainers.findIndex((value: any) => value.id === data.id);
    if (index > -1) {
      store.setProp({ key: 'typesContainers', index, value: data });
    }
    $toast?.success(t('ajustes.contenedores.tipo_de_contenedor_actualizado_con_exito'));
  } catch (error) {
    $toast?.error(t('global.ocurrio_un_error'));
  }

  handleClose();
};

const handleDeleteRestoreTypeContainer = async () => {
  if (selectedTypeContainer?.value?.id) {
    let response = null;
    try {
      if (isViewActive.value) {
        response = await TypesContainersRepository.deleteTypeContainer(
          selectedTypeContainer.value.id,
        );
      } else {
        response = await TypesContainersRepository.restoreTypeContainer(
          selectedTypeContainer.value.id,
        );
      }

      if (response === 200) {
        store.deleteItem({
          key: 'typesContainers',
          value: selectedTypeContainer?.value?.id,
        });
      }
    } catch (error) {
      if (isViewActive.value) {
        return $toast?.error(t('global.ocurrio_un_error'));
      }
      $toast?.error(t('global.ocurrio_un_error'));
    }

    handleClose();
  }
};
</script>

<template>
  <Layout :title="t('ajustes.contenedores.contenedores')" :textColor="'color-secondary'">
    <template #layout>
      <div class="d-flex justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>{{ t('global.activos') }}</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>{{ t('global.inactivos') }}</template>
          </CButton>
        </div>
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalCreate"
        >
          <template #content> {{ t('ajustes.contenedores.agregar_tipo_de_contenedor') }} </template>
        </CButton>
      </div>

      <DataTable
        :loading="isLoadingTypesContainers"
        :fields="isViewActive ? fieldsTableActive : fieldsTableInactive"
        :items="typeContainersPinia"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(typeContainerInfo)="typeContainerData">
          <div
            @click="handleSelectTypeContainer(typeContainerData.data.item)"
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-slot:cell(delete)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon"
          >
            <img src="/icons/basura.svg" alt="eliminar" />
          </div>
        </template>
        <template v-slot:cell(restore)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon restore"
          >
            <img src="/icons/restaurar.svg" alt="restore" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalCreateTypeContainer'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputTypeContainer"
            @onSubmit="handleCreateTypeContainer"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span> {{ t('ajustes.contenedores.agregar_tipo_de_contenedor') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateTypeContainer'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputTypeContainer"
            @onSubmit="handleUpdateTypeContainer"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('global.actualizar') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalDialog'">
          <h5 class="text-center">
            {{
              isViewActive
                ? `${t('ajustes.contenedores.esta_seguro_que_desea_eliminar_el_contenedor')} ${selectedTypeContainer?.typeName}?`
                : `${t('ajustes.contenedores.esta_seguro_que_desea_restaurar_el_contenedor')} } ${selectedTypeContainer?.typeName}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleDeleteRestoreTypeContainer"
            >
              <template #content>
                <div>
                  <span>{{ t('global.confirmar') }}</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
